import { makeAutoObservable, runInAction } from "mobx";
import { nanoid } from "nanoid";

import type { MenuCurrency } from "@/api/admin/brands/menuTypes";
import type { BrandFormMenuType } from "@/components/units";
import { toUnavailableBranchIds } from "@/components/units/BrandFormMenu/menuMappers";
import type {
  CategoryType,
  DishFormType,
} from "@/components/units/BrandFormMenu/types";
import {
  type BranchAvailability,
  DishDrawerStore,
} from "@/components/units/DishDrawer";
import { Tastes } from "@/modules/mocksData";

export const defaultDish: DishFormType = {
  dishNameEnglish: "",
  dishNameArabic: "",
  descriptionEnglish: "",
  descriptionArabic: "",
  preparationTime: "",
  price: "",
  active: false,
  unavailableBranchIds: [],
  flagsAndStatuses: [],
  meal: [],
  occasion: [],
  ratings: Tastes,
  dietTags: [],
  allergenTags: [],
  calories: "",
  burnCaloriesTime: "",
  protein: "",
  fiber: "",
  totalFat: "",
  totalSugar: "",
  saturatedFat: "",
  addedSugars: "",
  transFat: "",
  salt: "",
  cholesterol: "",
  sodium: "",
  carbohydrate: "",
  caffeine: "",
  saltContent: false,
  images: { media: [] },
};

type DishBranchMap = Record<string, BranchAvailability[]>;

export class MenuStore {
  menu: BrandFormMenuType = { cuisine: [], categories: [] };
  currentCategoryId: string | null = null;
  currentDish: DishFormType | null = null;
  branches: Omit<BranchAvailability, "available">[] = [];
  dishBranchAvailability: DishBranchMap = {};
  dishDrawer = new DishDrawerStore();

  /**
   * The currency this menu's prices are written in, from the brand. Drives how
   * many decimals the price field accepts and which symbol is shown next to a
   * price. Null until the menu is loaded, or when the brand's currency is one
   * we have no details for.
   */
  currency: MenuCurrency | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  private getCategoryById(id: string): CategoryType | undefined {
    return (this.menu.categories ?? []).find(c => c.id === id);
  }

  initMenu(menu: Partial<BrandFormMenuType>) {
    this.menu = {
      cuisine: menu.cuisine ?? this.menu.cuisine,
      categories: menu.categories ?? [],
      sourceFileName: menu.sourceFileName,
      sourceFileUrl: menu.sourceFileUrl,
      sourceMenuUrl: menu.sourceMenuUrl,
      menuId: menu.menuId,
      isActive: menu.isActive,
      updatedAt: menu.updatedAt,
    };
  }

  setCurrency(currency: MenuCurrency | null) {
    this.currency = currency;
  }

  /** Decimals the price field should accept, defaulting to 2 when unknown. */
  get priceDecimals(): number {
    return this.currency?.decimals ?? 2;
  }

  setBranches(branches: Omit<BranchAvailability, "available">[]) {
    this.branches = branches;
  }

  get totalBranches() {
    return this.branches.length;
  }

  setCurrentCategory(id: string) {
    this.currentCategoryId = id;
  }

  updateCategoryOrder(newOrder: string[]) {
    const currentCategories = this.menu.categories ?? [];
    const categoryMap = new Map(currentCategories.map(c => [c.id, c]));
    const reordered = newOrder
      .map(id => categoryMap.get(id))
      .filter((c): c is CategoryType => !!c);
    this.menu.categories = reordered;
  }

  setMenu(menu: Partial<BrandFormMenuType>) {
    const existingCategories = this.menu.categories ?? [];
    const incomingCategories = menu.categories ?? [];

    const merged = [...existingCategories];
    for (const incoming of incomingCategories) {
      const idx = merged.findIndex(c => c.id === incoming.id);
      if (idx >= 0) {
        merged[idx] = { ...merged[idx], ...incoming };
      } else {
        merged.push(incoming);
      }
    }

    this.menu = {
      ...this.menu,
      cuisine: menu.cuisine ?? this.menu.cuisine,
      categories: merged,
    };
  }

  addCategory(categoryNameEn: string, categoryNameAr?: string) {
    runInAction(() => {
      const id = nanoid();
      const categories = this.menu.categories ?? [];
      this.menu.categories = [
        ...categories,
        { id, categoryNameEn, categoryNameAr, dishes: [] },
      ];
    });
  }

  upsertCategory(category: CategoryType) {
    runInAction(() => {
      const categories = this.menu.categories ?? [];
      const index = categories.findIndex(c => c.id === category.id);
      if (index >= 0) {
        const next = [...categories];
        next[index] = { ...next[index], ...category };
        this.menu.categories = next;
      } else {
        this.menu.categories = [...categories, category];
      }
    });
  }

  updateCategory(
    categoryId: string,
    newCategoryNameEn: string,
    newCategoryNameAr?: string
  ) {
    runInAction(() => {
      const categories = this.menu.categories ?? [];
      // First apply the rename
      const renamed = categories.map(c =>
        c.id === categoryId
          ? {
              ...c,
              categoryNameEn: newCategoryNameEn,
              categoryNameAr: newCategoryNameAr,
            }
          : c
      );
      // Then recompute isDuplicate for ALL categories reactively
      const nameCounts = renamed.reduce<Record<string, number>>((acc, c) => {
        const key = c.categoryNameEn.trim().toLowerCase();
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
      }, {});
      this.menu.categories = renamed.map(c => ({
        ...c,
        isDuplicate: nameCounts[c.categoryNameEn.trim().toLowerCase()] > 1,
      }));
    });
  }

  clearCategoryDuplicate(categoryId: string) {
    runInAction(() => {
      const categories = this.menu.categories ?? [];
      this.menu.categories = categories.map(c =>
        c.id === categoryId ? { ...c, isDuplicate: false } : c
      );
    });
  }

  removeCategory(categoryId: string) {
    runInAction(() => {
      const category = this.getCategoryById(categoryId);
      if (category) {
        for (const dish of category.dishes) {
          if (dish.id) {
            delete this.dishBranchAvailability[dish.id];
          }
        }
      }
      this.menu.categories = (this.menu.categories ?? []).filter(
        c => c.id !== categoryId
      );
      if (this.currentCategoryId === categoryId) {
        this.currentCategoryId = null;
      }
    });
  }

  getCurrentDish() {
    return this.currentDish ?? defaultDish;
  }

  setCurrentDish(categoryId: string, dishId: string) {
    const category = this.getCategoryById(categoryId);
    if (!category) {
      this.currentDish = defaultDish;
      return;
    }
    const dish = category.dishes.find(d => d.id === dishId);
    this.currentDish = dish ?? defaultDish;
  }

  clearCurrentDish() {
    this.currentDish = null;
  }

  addOrUpdateDishToCurrentCategory(
    dish: DishFormType & { id?: string },
    branchAvailability?: BranchAvailability[],
    categoryId?: string
  ) {
    runInAction(() => {
      const dishId = dish.id ?? nanoid();

      const unavailableBranchIds = branchAvailability
        ? toUnavailableBranchIds(branchAvailability)
        : (dish.unavailableBranchIds ?? []);

      const updatedDish: DishFormType = {
        ...dish,
        id: dishId,
        unavailableBranchIds,
      };

      const key = categoryId ?? this.currentCategoryId;
      if (!key) return;

      const categories = this.menu.categories ?? [];
      this.menu.categories = categories.map(c => {
        if (c.id !== key) return c;

        const existingIndex = c.dishes.findIndex(d => d.id === dishId);
        let updatedDishes: DishFormType[];

        if (existingIndex >= 0) {
          updatedDishes = [...c.dishes];
          updatedDishes[existingIndex] = updatedDish;
        } else {
          updatedDishes = [...c.dishes, updatedDish];
        }

        return { ...c, dishes: updatedDishes };
      });

      if (branchAvailability) {
        this.dishBranchAvailability[dishId] = branchAvailability;
      }
    });
  }

  updateDishOrder(categoryId: string, orderedDishIds: string[]) {
    const categories = this.menu.categories ?? [];
    this.menu.categories = categories.map(c => {
      if (c.id !== categoryId) return c;
      const reorderedDishes = orderedDishIds
        .map(id => c.dishes.find(d => d.id === id))
        .filter((d): d is DishFormType => !!d);
      return { ...c, dishes: reorderedDishes };
    });
  }

  removeDishFromCategory(dishId?: string, categoryId?: string) {
    const id = dishId ?? this.currentDish?.id;
    const key = categoryId ?? this.currentCategoryId ?? "";

    if (!id) {
      this.clearCurrentDish();
      return;
    }

    const categories = this.menu.categories ?? [];
    this.menu.categories = categories.map(c => {
      if (c.id !== key) return c;
      return { ...c, dishes: c.dishes.filter(d => d.id !== id) };
    });

    delete this.dishBranchAvailability[id];
    this.clearCurrentDish();
  }

  updateDishActive(dishId: string, active: boolean) {
    runInAction(() => {
      const categories = this.menu.categories ?? [];
      this.menu.categories = categories.map(c => {
        const dishIndex = c.dishes.findIndex(d => d.id === dishId);
        if (dishIndex < 0) return c;
        const updatedDishes = [...c.dishes];
        updatedDishes[dishIndex] = {
          ...updatedDishes[dishIndex],
          active,
        };
        return { ...c, dishes: updatedDishes };
      });
    });
  }

  openCreateDish(categoryId: string) {
    this.currentCategoryId = categoryId;
    this.dishDrawer.openForCreate(categoryId, this.branches);
  }

  openEditDish(categoryId: string, dish: DishFormType) {
    this.currentCategoryId = categoryId;

    const branchAvailability = this.branches.map(b => ({
      ...b,
      available: !(dish.unavailableBranchIds ?? []).includes(b.branchId),
    }));

    if (dish.id) {
      this.dishBranchAvailability[dish.id] = branchAvailability;
    }

    this.dishDrawer.openForEdit(
      categoryId,
      dish,
      this.branches,
      branchAvailability
    );
  }

  onDrawerSave = (
    dish: DishFormType,
    branchAvailability: BranchAvailability[]
  ) => {
    const categoryId =
      this.dishDrawer.categoryKey ?? this.currentCategoryId ?? undefined;
    this.addOrUpdateDishToCurrentCategory(
      dish,
      branchAvailability,
      categoryId ?? undefined
    );
  };

  onDrawerDelete = () => {
    const dishId = this.dishDrawer.dish.id;
    const categoryId =
      this.dishDrawer.categoryKey ?? this.currentCategoryId ?? undefined;
    this.removeDishFromCategory(dishId, categoryId ?? undefined);
  };

  getBranchScopeLabel(dishId: string): string {
    const total = this.totalBranches;
    const branchAvailability = this.dishBranchAvailability[dishId];
    if (!branchAvailability || branchAvailability.length === 0) {
      return total > 0 ? "All branches" : "No branches";
    }
    const availableCount = branchAvailability.filter(b => b.available).length;
    if (availableCount === 0) return "Not available";
    if (availableCount === total) return "All branches";
    return `${availableCount} branch${availableCount > 1 ? "es" : ""}`;
  }

  clearMenu() {
    runInAction(() => {
      this.menu = { cuisine: [], categories: [] };
      this.currentCategoryId = null;
      this.currentDish = null;
      this.dishBranchAvailability = {};
      this.dishDrawer._forceClose();
    });
  }

  clearStore() {
    runInAction(() => {
      this.clearMenu();
      this.branches = [];
    });
  }
}
